import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import CustomButton from 'components/button/CustomButton';
import { LanguageChip } from 'components/training';
import {
  QuestionListResult,
  QuestionSourceType,
  listQuestion,
} from 'stores/TrainingAdminStoreAdapter';
import { errorDefault } from 'utils/Toastify';

type Props = {
  open: boolean;
  onClose: () => void;
  /** 既に課題に含まれる設問ID（重複追加不可・候補から除外表示） */
  existingQuestionIds: string[];
  /** 追加確定時に選択された設問IDを返す */
  onAdd: (questionIds: string[]) => void;
  /** 親の追加処理中フラグ（二重送信抑止・ボタン無効化） */
  submitting?: boolean;
};

/** 課題に設問を追加するダイアログ。設問一覧（非表示除外）から複数選択する。 */
const AddQuestionsDialog: React.FC<Props> = ({
  open,
  onClose,
  existingQuestionIds,
  onAdd,
  submitting = false,
}) => {
  const [questions, setQuestions] = React.useState<QuestionListResult[]>([]);
  const [keyword, setKeyword] = React.useState('');
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [loading, setLoading] = React.useState(false);

  const fetch = React.useCallback((kw: string) => {
    setLoading(true);
    listQuestion({ keyword: kw || undefined })
      .then(setQuestions)
      .catch(() => errorDefault('設問一覧の取得に失敗しました'))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setKeyword('');
      fetch('');
    }
  }, [open, fetch]);

  const existing = React.useMemo(() => new Set(existingQuestionIds), [existingQuestionIds]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = () => {
    if (submitting) return;
    if (selectedIds.size === 0) {
      errorDefault('追加する設問を選択してください');
      return;
    }
    onAdd(Array.from(selectedIds));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: 1, borderColor: 'divider' } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          設問を追加
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          placeholder="タイトルで検索"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') fetch(keyword);
          }}
          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'auto',
            maxHeight: '50vh',
            minHeight: 160,
          }}
        >
          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                読み込み中...
              </Typography>
            </Box>
          ) : questions.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                設問がありません
              </Typography>
            </Box>
          ) : (
            questions.map((q) => {
              const alreadyAdded = existing.has(q.questionId);
              return (
                <Box
                  key={q.questionId}
                  onClick={() => !alreadyAdded && toggle(q.questionId)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderBottom: 1,
                    borderColor: 'divider',
                    cursor: alreadyAdded ? 'default' : 'pointer',
                    opacity: alreadyAdded ? 0.5 : 1,
                    '&:hover': { bgcolor: alreadyAdded ? 'transparent' : 'action.hover' },
                  }}
                >
                  <Checkbox
                    size="small"
                    disabled={alreadyAdded}
                    checked={alreadyAdded || selectedIds.has(q.questionId)}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {q.title}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={QuestionSourceType[q.sourceType]}
                        variant="outlined"
                        sx={{ borderRadius: 1, height: 18, fontSize: 10 }}
                      />
                      {q.extensions.map((ext) => (
                        <LanguageChip key={ext} extension={ext} />
                      ))}
                    </Stack>
                  </Box>
                  {alreadyAdded && <Chip size="small" label="追加済み" sx={{ borderRadius: 1 }} />}
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CustomButton
          label="キャンセル"
          buttonVariant="secondary"
          onClick={onClose}
          disabled={submitting}
        />
        <CustomButton
          label={`追加（${selectedIds.size}件）`}
          buttonVariant="primary"
          onClick={handleAdd}
          disabled={submitting}
        />
      </DialogActions>
    </Dialog>
  );
};

export default AddQuestionsDialog;
